import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { WorldResolveShader } from './WorldResolveShader.js';

/**
 * Everything that needs the world's depth buffer, in one pass.
 *
 * The world is rendered into a private HDR target that carries a depth texture;
 * ambient occlusion and camera motion blur consume that depth; the finished
 * world image is then resolved into the composer's read buffer for the viewmodel
 * to be composited on top of. Keeping these inside one pass is what stops the
 * gun from getting an AO halo — it simply does not exist yet when AO runs.
 *
 * It also sidesteps a real trap: EffectComposer ping-pongs its two targets and
 * never resets the parity between frames, so "the depth of the buffer the world
 * landed in" is not a stable thing for a later pass to reference. Owning the
 * target outright makes the depth unambiguous.
 */
export class WorldStagePass extends Pass {
  /**
   * @param {THREE.Scene} scene
   * @param {THREE.PerspectiveCamera} camera
   * @param {object} opts { ao, motionBlur, aoParams, pdParams }
   */
  constructor(scene, camera, opts = {}) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.needsSwap = false; // we deliver the result into readBuffer ourselves

    this.aoEnabled = !!opts.ao;
    this.motionBlurEnabled = !!opts.motionBlur;
    this.aoIntensity = 0.8;
    // AO is a low-frequency signal and the poisson denoise blurs it anyway, so
    // resolving it at half res costs a quarter of the samples and looks the
    // same. At 4K this is the difference between ~16 ms and ~4 ms — by far the
    // most expensive thing in the stack if you let it run at native.
    this.aoScale = opts.aoScale ?? 0.5;
    this.motionBlurStrength = 0.55;
    this.maxBlurPixels = 11;

    this.width = 1;
    this.height = 1;

    /* ---- world colour + depth ------------------------------------------- */
    this.depthTexture = new THREE.DepthTexture(1, 1);
    this.depthTexture.format = THREE.DepthFormat;
    this.depthTexture.type = THREE.UnsignedIntType;
    this.depthTexture.minFilter = THREE.NearestFilter;
    this.depthTexture.magFilter = THREE.NearestFilter;

    this.worldRT = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
      depthTexture: this.depthTexture,
    });
    this.worldRT.texture.name = 'PostFX.world';

    this.gtao = null;
    if (this.aoEnabled) this._buildGTAO(opts.aoParams, opts.pdParams);

    /* ---- resolve (AO multiply + motion blur) ---------------------------- */
    this.resolveMaterial = new THREE.ShaderMaterial({
      name: WorldResolveShader.name,
      defines: { ...WorldResolveShader.defines },
      uniforms: THREE.UniformsUtils.clone(WorldResolveShader.uniforms),
      vertexShader: WorldResolveShader.vertexShader,
      fragmentShader: WorldResolveShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.resolveMaterial.uniforms.tDiffuse.value = this.worldRT.texture;
    this.resolveMaterial.uniforms.tDepth.value = this.depthTexture;
    if (this.gtao) this.resolveMaterial.uniforms.tAO.value = this.gtao.gtaoMap;

    this.fsQuad = new FullScreenQuad(this.resolveMaterial);

    this._curViewProj = new THREE.Matrix4();
    this._prevViewProj = new THREE.Matrix4();
    this._hasPrev = false;
  }

  /**
   * Two things are being worked around here.
   *
   * One: GTAOPass insists on building its own normal G-buffer, which means a
   * second full scene render every frame. We hand it our depth texture instead
   * and let the shader reconstruct view normals from depth
   * (NORMAL_VECTOR_TYPE 0) — on the flat, boxy geometry of a shooter level that
   * reconstruction is essentially exact, and it buys back an entire scene pass.
   * `setGBuffer` reads `normalRenderTarget.depthTexture` on its way out, so it
   * can only be called *after* the constructor has made one: build normally,
   * re-point, then throw the normal target away.
   *
   * Two: OUTPUT.Off makes the pass stop at the denoised AO map. Its Default
   * output would copy the colour buffer into a second HDR target and multiply
   * AO over it — two extra fullscreen draws and 45 MB at 4K to do what one
   * multiply in the resolve shader already does.
   */
  _buildGTAO(aoParams, pdParams) {
    const gtao = new GTAOPass(this.scene, this.camera, this.width, this.height);
    gtao.setGBuffer(this.depthTexture, null);

    gtao.normalRenderTarget.dispose();
    gtao.normalRenderTarget = { setSize() {}, dispose() {}, depthTexture: null };
    gtao.output = GTAOPass.OUTPUT.Off;

    if (aoParams) gtao.updateGtaoMaterial(aoParams);
    if (pdParams) gtao.updatePdMaterial(pdParams);

    this.gtao = gtao;
  }

  get aoActive() {
    return this.aoEnabled && this.gtao !== null;
  }

  setSize(width, height) {
    this.width = Math.max(1, Math.floor(width));
    this.height = Math.max(1, Math.floor(height));
    this.worldRT.setSize(this.width, this.height);
    if (this.gtao) {
      this.gtao.setSize(
        Math.max(1, Math.round(this.width * this.aoScale)),
        Math.max(1, Math.round(this.height * this.aoScale)),
      );
    }
    this._applyBlurClamp();
  }

  /** Motion blur clamp in pixels, so it reads the same at any resolution. */
  setMaxBlurPixels(px) {
    this.maxBlurPixels = px;
    this._applyBlurClamp();
  }

  _applyBlurClamp() {
    this.resolveMaterial.uniforms.uMaxVelocity.value.set(
      this.maxBlurPixels / this.width,
      this.maxBlurPixels / this.height,
    );
  }

  render(renderer, writeBuffer, readBuffer, deltaTime) {
    const oldAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    renderer.setRenderTarget(this.worldRT);
    renderer.clear(true, true, false);
    renderer.render(this.scene, this.camera);

    const u = this.resolveMaterial.uniforms;

    if (this.aoActive) {
      // writeBuffer/readBuffer are unused with OUTPUT.Off, but the signature
      // wants them.
      this.gtao.render(renderer, this.worldRT, this.worldRT, deltaTime);
      u.tAO.value = this.gtao.gtaoMap;
      u.uAOIntensity.value = this.aoIntensity;
    } else {
      u.uAOIntensity.value = 0;
    }

    if (this.motionBlurEnabled) {
      u.uStrength.value = this.motionBlurStrength;
      u.uInvViewProj.value.copy(this._viewProj()).invert();
      u.uPrevViewProj.value.copy(this._hasPrev ? this._prevViewProj : this._curViewProj);
    } else {
      u.uStrength.value = 0;
    }

    renderer.setRenderTarget(this.renderToScreen ? null : readBuffer);
    this.fsQuad.render(renderer);

    renderer.autoClear = oldAutoClear;
    this._captureMatrices();
  }

  _viewProj() {
    return this._curViewProj.multiplyMatrices(
      this.camera.projectionMatrix,
      this.camera.matrixWorldInverse,
    );
  }

  _captureMatrices() {
    this._prevViewProj.copy(this._viewProj());
    this._hasPrev = true;
  }

  dispose() {
    this.worldRT.dispose();
    this.depthTexture.dispose();
    this.resolveMaterial.dispose();
    this.fsQuad.dispose();
    if (this.gtao) this.gtao.dispose();
  }
}
